import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

/** Formato exato que a tabela espera — inclui os enums de quadro. Evita `any`
 *  e faz o compilador cobrar o preenchimento correto. */
type HistoricoStatusInsert = Database['public']['Tables']['historico_status']['Insert'];
/** `quadro_enum` do banco — os campos de quadro do histórico são tipados. */
type QuadroEnum = Database['public']['Enums']['quadro_enum'];
import { useToast } from '@/hooks/use-toast';

interface CancelarItemParams {
  itemId: string;
  motivoIds: string[];
  statusId: string;
  observacao?: string;
}

export function useCancelamentoItem() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: cancelarItem, isPending } = useMutation({
    mutationFn: async ({ itemId, motivoIds, statusId, observacao }: CancelarItemParams) => {
      console.log('🔴 [CANCELAR] ========== INÍCIO DO CANCELAMENTO ==========');
      console.log('🔴 [CANCELAR] Dados recebidos:', { itemId, motivoIds, statusId, observacao });
      
      // getSession é LOCAL (não vai à rede) — o getUser validava o token no
      // servidor e era a 1ª de ~8 idas em série que deixavam o "Cancelando…"
      // pendurado (Douglas 07/08: "demora pra cancelar").
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      console.log('🔴 [CANCELAR] Usuário autenticado:', user?.id);
      
      if (!user) throw new Error('Usuário não autenticado');

      if (!observacao || observacao.length < 20) {
        throw new Error('Justificativa deve ter no mínimo 20 caracteres');
      }

      // Buscar estado atual COMPLETO do item antes de cancelar
      console.log('🔴 [CANCELAR] Buscando estado atual do item...');
      const { data: itemAnterior, error: itemError } = await supabase
        .from('itens_pedido')
        .select('id, pedido_id, produto_id, quadro_atual, coluna_atual, status_id, data_cancelamento')
        .eq('id', itemId)
        .single();

      if (itemError) {
        console.error('❌ [CANCELAR] Erro ao buscar item:', itemError);
        throw itemError;
      }

      console.log('🔴 [CANCELAR] Estado atual do item:', itemAnterior);

      // Inserir cancelamento para cada motivo selecionado
      console.log('🔴 [CANCELAR] Inserindo registros em item_cancelamentos...');
      let lastData: any = null;

      for (const motivoId of motivoIds) {
        const { data, error } = await supabase
          .from('item_cancelamentos')
          .insert({
            item_id: itemId,
            motivo_id: motivoId,
            status_cancelamento_id: statusId,
            cancelado_por: user.id,
            observacao: observacao,
            cancelado_em: new Date().toISOString(),
            snapshot_anterior: itemAnterior,
          })
          .select()
          .single();

        if (error) {
          console.error('❌ [CANCELAR] Erro ao inserir motivo:', motivoId, error);
          throw error;
        }

        console.log('✅ [CANCELAR] Motivo registrado:', motivoId, data);
        lastData = data;
      }

      // Verificar se item continua no mesmo pedido (VALIDAÇÃO CRÍTICA).
      // As três leituras abaixo são independentes — em paralelo, uma ida só.
      console.log('🔴 [CANCELAR] Verificando se item permaneceu no pedido original...');
      const [{ data: itemDepois }, { data: statusDestino }, { data: motivoInfo }] = await Promise.all([
        supabase.from('itens_pedido')
          .select('id, pedido_id, data_cancelamento, status_id')
          .eq('id', itemId)
          .single(),
        supabase.from('status_catalogo').select('nome, quadro').eq('id', statusId).maybeSingle(),
        supabase.from('motivos_cancelamento').select('titulo').eq('id', motivoIds[0]).maybeSingle(),
      ]);

      console.log('🔴 [CANCELAR] Estado do item APÓS cancelamento:', itemDepois);

      if (itemDepois?.pedido_id !== itemAnterior.pedido_id) {
        console.error('❌❌❌ [ERRO CRÍTICO] ITEM FOI MOVIDO PARA OUTRO PEDIDO!');
        console.error('Pedido ANTES:', itemAnterior.pedido_id);
        console.error('Pedido DEPOIS:', itemDepois?.pedido_id);
      } else {
        console.log('✅ [CANCELAR] Item permaneceu no pedido correto:', itemDepois?.pedido_id);
      }

      // 📜 HISTÓRICO (Douglas 30/07: "isso temos que estar muito bem alinhado
      // e fazer histórico").
      //
      // Até aqui o cancelamento gravava SÓ em `item_cancelamentos`. A tabela
      // `historico_status` — que é o rastro que a timeline e o Hora a Hora leem
      // — não recebia nada: as transições para PEDIDO_CANCELADO eram ZERO no
      // histórico. Por isso "onde o pedido morreu" só existia dentro do
      // `snapshot_anterior` do cancelamento, e nenhuma tela de movimentação
      // enxergava o cancelamento como um movimento.
      //
      // Fire-and-forget de propósito: o cancelamento em si já está gravado, e
      // uma falha aqui não pode desfazer nem travar a ação do usuário.
      try {
        await supabase.from('historico_status').insert({
          pedido_id: itemAnterior.pedido_id,
          item_id: itemId,
          quadro_anterior: itemAnterior.quadro_atual as QuadroEnum,
          quadro_atual: (statusDestino?.quadro as QuadroEnum) ?? 'PEDIDO_CANCELADO',
          coluna_anterior: itemAnterior.coluna_atual,
          coluna_atual: statusDestino?.nome || 'PEDIDO CANCELADO',
          status_anterior_id: itemAnterior.status_id,
          status_atual_id: statusId,
          user_id: user.id,
          motivo: motivoInfo?.titulo || 'Cancelamento',
          observacoes: observacao,
        } satisfies HistoricoStatusInsert);
        console.log('✅ [CANCELAR] Histórico registrado');
      } catch (histErr) {
        console.error('[CANCELAR] Falha ao registrar histórico (cancelamento mantido):', histErr);
      }

      console.log('🔴 [CANCELAR] ========== FIM DO CANCELAMENTO ==========');

      // No Contrato e na Tramitação o banco cancela o pedido inteiro (Douglas
      // 10/10/2026): a linha que alguém cancelou leva as outras junto.
      const pedidoInteiro = ['CONTRATO_PRIME', 'TRAMITACAO_PRIME', 'TRAMITACAO_VIVO'].includes(String(itemAnterior.quadro_atual));
      return { ...lastData, pedidoInteiro };
    },
    onSuccess: async (data, variables) => {
      toast({
        title: data?.pedidoInteiro ? 'Pedido cancelado' : 'Item cancelado com sucesso',
        description: data?.pedidoInteiro
          ? 'As linhas do pedido foram canceladas juntas. Para seguir com alguma, recupere só ela.'
          : 'Atualizando status...',
      });
      
      // 🎯 Invalidar SEM await (Douglas 07/08): o await segurava o
      // "Cancelando…" até a lista inteira refazer a consulta na planilha-mãe.
      // O refetch continua acontecendo — só não trava mais o botão.
      queryClient.invalidateQueries({ 
        queryKey: ['item-completo', variables.itemId],
        refetchType: 'active' 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['pedido-itens-completo'],
        refetchType: 'all' 
      });
      
      // Invalidar outras queries relacionadas
      queryClient.invalidateQueries({ 
        queryKey: ['pedidos'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({ 
        queryKey: ['itens-pedido'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({ 
        queryKey: ['planilha-itens'],
        refetchType: 'active'
      });
      // Banner do painel (Douglas 16/06): refresca cancelamentos + pedido na hora
      queryClient.invalidateQueries({ queryKey: ['cancelamentos-pedido'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['pedido-detail'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['item-completo'], refetchType: 'active' });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao cancelar item',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    cancelarItem,
    isCancelando: isPending,
  };
}
